import * as THREE from 'three';
import { clamp } from '../utils.js';

const _euler = new THREE.Euler(0, 0, 0, 'YXZ');
const _fwd = new THREE.Vector3();
const _spinDelta = new THREE.Quaternion();

/**
 * Kinematic helicopter flight model.
 *
 * State is driven by three AI-set targets (heading, forward speed, vertical
 * rate) plus per-axis rate limits. Visual pitch and roll are derived from
 * forward speed and yaw rate respectively — no force/torque integration.
 */
export default class HelicopterFlightModel {
  constructor(params) {
    this.params = params;

    // Physics state
    this.position = new THREE.Vector3();
    this.velocity = new THREE.Vector3();
    this.heading = 0;          // yaw, radians, world frame
    this.forwardSpeed = 0;     // m/s along heading
    this.verticalRate = 0;     // m/s
    this.yawRate = 0;          // rad/s (computed each frame; used for banking)

    // Visual state (spring-damper integrated — gives momentum feel)
    this.pitch = 0;
    this.roll = 0;
    this.pitchVel = 0;
    this.rollVel = 0;

    // Sum-of-sines sway state. Per-instance random phase so multiple birds
    // don't visibly oscillate in lock-step.
    this._swayTime = 0;
    this._swayPhase = [
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
    ];

    // AI inputs — set externally each frame before update()
    this.desiredHeading = 0;
    this.desiredSpeed = 0;
    this.desiredVerticalRate = 0;

    // Damage modulation (set externally per-frame)
    this.liftScale = 1;        // multiplies achievable upward verticalRate (1 = normal)
    this.structuralFailure = false;
    this.angularVelocity = new THREE.Vector3(); // rad/s, used only in failure mode
    this.failureGravity = 9.81; // m/s² — overridden by EnemyHelicopter when failure begins

    this.quaternion = new THREE.Quaternion();
  }

  update(dt) {
    if (this.structuralFailure) {
      this._updateFailure(dt);
      return;
    }
    const p = this.params;

    // 1. Heading — chase a desired yaw rate (proportional to error, capped at
    //    maxYawRate). Real angular momentum: yawRate accelerates toward the
    //    target with a yawAccel cap, so the heli eases into and out of turns
    //    instead of snapping to maxYawRate the instant a heading change comes in.
    let headingErr = this.desiredHeading - this.heading;
    while (headingErr >  Math.PI) headingErr -= Math.PI * 2;
    while (headingErr < -Math.PI) headingErr += Math.PI * 2;
    const desiredYawRate = clamp(headingErr * p.yawRateGain, -p.maxYawRate, p.maxYawRate);
    const yawAccelMax = p.yawAccel * dt;
    this.yawRate += clamp(desiredYawRate - this.yawRate, -yawAccelMax, yawAccelMax);
    this.heading += this.yawRate * dt;

    // 2. Forward speed — chase desiredSpeed, limited by forwardAccel
    const speedErr = this.desiredSpeed - this.forwardSpeed;
    const maxSpeedDelta = p.forwardAccel * dt;
    this.forwardSpeed += clamp(speedErr, -maxSpeedDelta, maxSpeedDelta);
    this.forwardSpeed = clamp(this.forwardSpeed, p.minSpeed, p.maxSpeed);

    // 3. Vertical rate — chase desiredVerticalRate, limited by verticalAccel.
    //    liftScale degrades the upward ceiling/accel only — descent still works
    //    so a damaged heli sags and can't arrest its fall.
    const desiredV = Math.min(this.desiredVerticalRate, p.maxClimbRate * this.liftScale);
    const vertErr = desiredV - this.verticalRate;
    const maxVertDelta = p.verticalAccel * dt;
    this.verticalRate += clamp(vertErr, -maxVertDelta, maxVertDelta);
    this.verticalRate = clamp(
      this.verticalRate,
      -p.maxDescentRate,
      p.maxClimbRate * this.liftScale,
    );

    // 4. World velocity from heading + forward speed + vertical rate.
    // Aircraft "forward" convention is -Z at zero heading (matches player Aircraft).
    _fwd.set(-Math.sin(this.heading), 0, -Math.cos(this.heading));
    this.velocity.copy(_fwd).multiplyScalar(this.forwardSpeed);
    this.velocity.y = this.verticalRate;

    // 5. Integrate position
    this.position.addScaledVector(this.velocity, dt);

    // 6. Visual pitch — nose-down lean proportional to forward speed.
    //    Spring-damper integration: F = -k(x - target) - c·v. This gives the
    //    pitch real "mass" — it lags inputs, overshoots slightly when damping
    //    is sub-critical, and settles instead of snapping.
    const pitchTarget = -(this.forwardSpeed / p.maxSpeed) * p.forwardLeanMax;
    const pitchAccel = -p.pitchStiffness * (this.pitch - pitchTarget) - p.pitchDamping * this.pitchVel;
    this.pitchVel += pitchAccel * dt;
    this.pitch    += this.pitchVel * dt;

    // 7. Visual roll — bank into turns proportional to yaw rate, again as a
    //    spring-damper. Since yawRate is itself momentum-smoothed (step 1),
    //    rollTarget is already gentle; the spring adds a second layer of
    //    inertia so the heli "leans" into and out of turns naturally.
    const rollTarget = clamp(
      this.yawRate * p.bankPerYawRate,
      -p.forwardLeanMax * 2, p.forwardLeanMax * 2,
    );
    const rollAccel = -p.rollStiffness * (this.roll - rollTarget) - p.rollDamping * this.rollVel;
    this.rollVel += rollAccel * dt;
    this.roll    += this.rollVel * dt;

    // 8. Random sway — sum-of-sines pseudo-noise. Two harmonics per axis at an
    //    irrational ratio (1.0× and 1.7×) so the pattern never repeats audibly.
    //    Applied as a visual offset on top of the spring state; flight model
    //    bookkeeping (heading/pitch/roll) is untouched so AI / followers see
    //    the "true" attitude.
    this._swayTime += dt;
    const t = this._swayTime;
    const f = p.swayFrequency * Math.PI * 2;
    const ph = this._swayPhase;
    const swayPitch = p.swayPitchAmp * (Math.sin(t * f       + ph[0]) + 0.5 * Math.sin(t * f * 1.7 + ph[1]));
    const swayRoll  = p.swayRollAmp  * (Math.sin(t * f * 1.1 + ph[2]) + 0.5 * Math.sin(t * f * 1.9 + ph[3]));
    const swayYaw   = p.swayYawAmp   * (Math.sin(t * f * 0.9 + ph[4]) + 0.5 * Math.sin(t * f * 1.5 + ph[5]));

    // 9. Compose quaternion (YXZ: yaw, then pitch, then roll) — apply sway last.
    _euler.set(this.pitch + swayPitch, this.heading + swayYaw, this.roll + swayRoll);
    this.quaternion.setFromEuler(_euler);
  }

  /**
   * Structural-failure update: rotor lift gone, gravity integrated into a
   * real vertical velocity, body spins under angularVelocity. AI inputs and
   * heading/pitch/roll bookkeeping are ignored while in this mode.
   */
  _updateFailure(dt) {
    // Free-fall + horizontal drift retained from last commanded velocity.
    this.velocity.y -= this.failureGravity * dt;
    this.position.addScaledVector(this.velocity, dt);

    // Apply angular velocity to the quaternion as a small-angle delta rotation.
    const wx = this.angularVelocity.x;
    const wy = this.angularVelocity.y;
    const wz = this.angularVelocity.z;
    const halfDt = dt * 0.5;
    _spinDelta.set(wx * halfDt, wy * halfDt, wz * halfDt, 1).normalize();
    this.quaternion.multiply(_spinDelta).normalize();
  }
}
